import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { UiKitModule } from '@serviceplan/ui-kit';
import {
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  FieldType,
  FormControlModel,
  OptionsLabelPipe,
  SpDateFormatPipe,
  StepperGroupModel,
  StepperModel,
  TableColumn,
  getRelationTypeName,
} from '@thehubz/client/core';
import { History, HistoryApiService, HistoryResultResponse } from '@thehubz/client/data-access-api';
import { map, tap } from 'rxjs';

import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, buyoutRightsObj } from '@thehubz/shared/model';
import { Channel, Product, SubCategory, Subject } from '../../customer/vorwerk/vorwerk-metadata-config';
import { AssetSettingsStepperConfigService } from '../asset-upload/stepper-config/asset-settings-stepper-config.service';
import { buyoutRightConfigs, buyoutRightConfigsSEAT, creatorDepartmentObj } from '../asset-upload/stepper-config/asset-settings-stepper.constants';
import { HistoryTableColumnData } from './constants/history-table-columns.constant';
import { getHistoryActionLabel } from './utils/history.util';

@Component({
  selector: 'thehubz-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
  standalone: true,
  imports: [CommonModule, UiKitModule],
})
export class HistoryComponent implements OnInit {
  @Input() tableType: History.EntityTypeEnum = History.EntityTypeEnum.Asset;
  @Input() resourceId = '';
  @Input() showHeader = false;

  httpCallInProgress = false;
  history: History[] = [];
  count = 0;
  columns!: TableColumn[];
  private allFormFields: FormControlModel[] = [];
  readonly brandingKey: CustomClients;

  pageType = $localize`:page-type:history`;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private historyApiService: HistoryApiService,
    private optionsLabel: OptionsLabelPipe,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private readonly assetSettingsStepperConfigService: AssetSettingsStepperConfigService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    this.columns = HistoryTableColumnData.TABLE_DEFAULT_COLUMNS;
    this.pullStepperDataObject();
    this._showLoader();
  }

  async pullStepperDataObject() {
    let stepperData: StepperModel;
    switch (this.brandingKey) {
      case CustomClients.Arval:
        stepperData = await this.assetSettingsStepperConfigService.getArvalAssetSettingsStepperConfig([], { disableTag: false });
        break;
      case CustomClients.VKB:
        stepperData = await this.assetSettingsStepperConfigService.getVKBAssetSettingsStepperConfig([], { disableTag: false });
        break;
      case CustomClients.SEAT:
        stepperData = await this.assetSettingsStepperConfigService.getSeatAssetSettingsStepperConfig([], { disableTag: false });
        break;
      case CustomClients.LufthansaMediacenter:
      case CustomClients.LufthansaMedialounge:
        stepperData = await this.assetSettingsStepperConfigService.getLufthansaAssetSettingsStepperConfig([], { disableTag: false });
        break;
      case CustomClients.Vorwerk:
        stepperData = await this.assetSettingsStepperConfigService.getVorwerkAssetSettingsStepperConfig([], { disableTag: false });
        break;
      case CustomClients.Allianz:
        stepperData = await this.assetSettingsStepperConfigService.getAllianzAssetSettingsStepperConfig([], { disableTag: false });
        break;
      default:
        stepperData = await this.assetSettingsStepperConfigService.getStandardAssetSettingsStepperConfig([], { disableTag: false });
        break;
    }
    const stepperMasterData = stepperData;
    stepperMasterData?.stepper.forEach((stepper: StepperGroupModel) => {
      const formFields: FormControlModel[] =
        stepper.formFields?.filter(
          (formField: FormControlModel) =>
            (formField.fieldType === FieldType.MultiSelectChip || formField.fieldType === FieldType.Dropdown) &&
            formField.options &&
            formField.options.length > 1,
        ) || [];

      this.allFormFields = [...this.allFormFields, ...formFields];
    });
    this.fetchAssetHistory();
  }

  fetchAssetHistory() {
    const filter = { filters: { entityType: this.tableType, referenceId: this.resourceId } };

    this.historyApiService
      .historyControllerFindAll(JSON.stringify(filter))
      .pipe(
        tap(() => {
          this._showLoader(false);
        }),
        map((res: HistoryResultResponse) => mapHistoryToUIModel(res, this.brandingKey)),
      )
      .subscribe({
        next: (res: UIHistoryResponseModel) => {
          if (res.result && res.result.length > 0) {
            const historyResult = res.result;
            historyResult.forEach((history: History) => {
              const propertyName = history.propertyName;
              const fieldIndex = this.allFormFields.findIndex((formField: FormControlModel) => formField.formFieldName === propertyName);
              if (fieldIndex != -1) {
                const options = this.allFormFields[fieldIndex].options;
                if (this.allFormFields[fieldIndex].fieldType === 'multiSelectChip') {
                  history.newValue = this.fetchData(history.newValue, options);
                  history.oldValue = this.fetchData(history.oldValue, options);
                } else {
                  history.newValue = this.optionsLabel.transform(history.newValue || '', options);
                  history.oldValue = this.optionsLabel.transform(history.oldValue || '', options);
                }
              }
            });
            this.history = historyResult;
            this.count = res.count;
          }
        },
        error: (err) => {
          this.clientNotificationHandlerService.errorNotification(err);
        },
      });
  }

  fetchData(data: AnyType, options: AnyType) {
    data = data.split(',');
    let returnString = '';
    data.forEach((value: AnyType) => {
      if (returnString != '') {
        returnString += ', ';
      }
      const transformedValue = value ? this.optionsLabel.transform(value, options) : '';
      returnString += transformedValue;
    });
    return returnString;
  }

  /**
   * Utility method to show/hide loader
   */
  private _showLoader(show = true) {
    this.httpCallInProgress = show;
  }
}

export interface UIHistoryResponseModel {
  count: number;
  result: UIHistoryModel[];
}

export interface UIHistoryModel extends History {
  action: string;
}

export function mapHistoryToUIModel(history: HistoryResultResponse, brandingKey?: CustomClients): UIHistoryResponseModel {
  return {
    count: history.count,
    result: history.result
      .map((history) => {
        let actionStr = '';
        const actionObj = getHistoryActionLabel(history.entityType, history.actionPerformed);
        if (typeof actionObj === 'object') {
          if (history.propertyName && actionObj[history.propertyName]) {
            actionStr = actionObj[history.propertyName];
          } else {
            actionStr = $localize`Values edited`;
          }
        } else {
          if (history.actionPerformed === History.ActionPerformedEnum.UsageRightsChanged) {
            if (history.propertyName?.includes('newLicenseRegion')) {
              const newValue = JSON.parse(history.newValue || '');
              history.newValue = formatLicenseRegion(newValue);
              actionStr = licenseRegionActionString(actionObj, history.propertyName, brandingKey);
            } else {
              actionStr = usageRightActionString(actionObj, history.propertyName, brandingKey);
            }
          } else {
            actionStr = actionObj;
          }
        }
        return {
          ...history,
          action: actionStr,
          oldValue: generateLabel(history.propertyName || '', stripHtmlFromString(history.oldValue || '') || '', brandingKey),
          newValue: generateLabel(history.propertyName || '', stripHtmlFromString(history.newValue || '') || '', brandingKey),
        };
      })
      .map((history) => {
        if (
          history.actionPerformed === History.ActionPerformedEnum.RelationCreated ||
          history.actionPerformed === History.ActionPerformedEnum.RelationRemoved
        ) {
          history.oldValue = history.oldValue ? getRelationTypeName(history.oldValue) : '';
          history.newValue = history.newValue ? getRelationTypeName(history.newValue) : '';
        }
        return history;
      }),
  };
}

export function formatLicenseRegion(input: AnyType) {
  const spDateFormat = new SpDateFormatPipe();
  const { licenseRegion, channel, buyoutType, noExpiry, expiryDate } = input;
  const baseStringParts: string[] = [];

  if (licenseRegion) baseStringParts.push(`License region: ${licenseRegion}`);
  if (channel) baseStringParts.push(`Channel: ${channel}`);
  if (buyoutType) baseStringParts.push(`Buyout type: ${buyoutType}`);

  const baseString = baseStringParts.length ? baseStringParts.join(', ') : undefined;
  const dateString = noExpiry ? 'No expiry set' : expiryDate ? `Expiry date: ${spDateFormat.transform(expiryDate)}` : 'No expiry set';

  return baseString ? `${baseString}, ${dateString}` : dateString;
}

export function licenseRegionActionString(actionObj: AnyType, propertyName?: string, brandingKey?: CustomClients) {
  const optionsLabelPipe = new OptionsLabelPipe();
  const splitPropertyName = propertyName?.split('/');
  const buyoutRightsTypes = buyoutRightsObj(brandingKey || CustomClients.Standard);
  const buyoutTypeLabel = optionsLabelPipe?.transform(splitPropertyName?.[1] || '', buyoutRightsTypes);
  return `${actionObj}: ${buyoutTypeLabel}: Updated license region`;
}

export function usageRightActionString(actionObj: AnyType, propertyName?: string, brandingKey?: CustomClients) {
  const optionsLabelPipe = new OptionsLabelPipe();
  const splitPropertyName = propertyName?.split('/');
  let buyoutRightsConfig: Partial<FormControlModel>[] = [];
  if (brandingKey === CustomClients.SEAT) buyoutRightsConfig = buyoutRightConfigsSEAT();
  else buyoutRightsConfig = buyoutRightConfigs(brandingKey || CustomClients.Standard);
  const propertyIndex = buyoutRightsConfig.findIndex((right) => right.formFieldName === splitPropertyName?.[1]);
  if (propertyIndex !== -1) {
    const fieldLabel = buyoutRightsConfig[propertyIndex].fieldLabel;
    const buyoutRightsTypes = buyoutRightsObj(brandingKey || CustomClients.Standard);
    const buyoutTypeLabel = optionsLabelPipe?.transform(splitPropertyName?.[0] || '', buyoutRightsTypes);
    return `${actionObj}: ${buyoutTypeLabel}: ${fieldLabel} edited`;
  }
  return actionObj;
}

export function stripHtmlFromString(html: string): string | undefined {
  if (!html) return;
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export function generateLabel(propertyName: string, value: string, brandingKey?: CustomClients) {
  if (!value) return;
  const optionsLabelPipe = new OptionsLabelPipe();
  const spDateFormat = new SpDateFormatPipe();
  const propName = propertyName.includes('/') ? propertyName?.split('/')[1] : propertyName;
  switch (propName) {
    case 'subject':
      return optionsLabelPipe?.transform(value, Subject);
    case 'channel':
      return optionsLabelPipe?.transform(value, Channel);
    case 'product':
      return optionsLabelPipe?.transform(value, Product);
    case 'subcategory':
      return optionsLabelPipe?.transform(value, SubCategory);
    case 'creatorDepartment':
      return optionsLabelPipe?.transform(value, creatorDepartmentObj(brandingKey || CustomClients.Standard));
    case 'newUsageRights':
    case 'usageRightDeleted':
      return optionsLabelPipe?.transform(value, buyoutRightsObj(brandingKey || CustomClients.Standard));
    default:
      if (isValidDate(value)) return spDateFormat.transform(value);
      else return value;
  }
}

function isValidDate(dateString: string): boolean {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.valueOf()) && date.toISOString() === dateString;
}
